import React, { useState, useEffect } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import api from '../utils/api';
import { FiClock, FiUsers, FiDownload, FiX, FiCalendar, FiTrendingUp } from 'react-icons/fi';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import toast from 'react-hot-toast';

const toInputDate = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const d = String(date.getDate()).padStart(2, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const y = date.getFullYear();
    return `${d}/${m}/${y}`;
};

const getMonthName = (mVal) => {
    if (mVal === 'all') return 'All Months';
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return months[parseInt(mVal) - 1] || mVal;
};

const getYearDisplay = (yVal) => {
    if (yVal === 'all') return 'All Year';
    return yVal;
};


const CompanyOverviewCharts = ({ userId }) => {
    const [trendData, setTrendData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [year, setYear] = useState(new Date().getFullYear());

    // Drill-down states
    const [showModal, setShowModal] = useState(false);
    const [details, setDetails] = useState([]);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [modalInfo, setModalInfo] = useState({ title: '', month: '' });
    const [activeTrendType, setActiveTrendType] = useState('total');

    // Independent State variables for Top Leaves Taken
    const [leaveMonth, setLeaveMonth] = useState(String(new Date().getMonth() + 1)); // e.g. '5' for May
    const [leaveYear, setLeaveYear] = useState(String(new Date().getFullYear()));   // e.g. '2026'
    const [leaveLimit, setLeaveLimit] = useState('5');
    const [leaveTypeFilter, setLeaveTypeFilter] = useState('all'); // 'all', 'scheduled', 'unscheduled'
    const [topLeaveEmployees, setTopLeaveEmployees] = useState([]);
    const [leaveTrendsLoading, setLeaveTrendsLoading] = useState(false);

    // Independent State variables for Top Late Comers
    const [lateMonth, setLateMonth] = useState(String(new Date().getMonth() + 1));   // e.g. '5' for May
    const [lateYear, setLateYear] = useState(String(new Date().getFullYear()));     // e.g. '2026'
    const [lateLimit, setLateLimit] = useState('5');
    const [topLateComers, setTopLateComers] = useState([]);
    const [lateTrendsLoading, setLateTrendsLoading] = useState(false);

    // Drilldown details states for Employee Trend charts
    const [showEmpDrilldown, setShowEmpDrilldown] = useState(false);
    const [empDrilldownLoading, setEmpDrilldownLoading] = useState(false);
    const [empDrilldownData, setEmpDrilldownData] = useState([]);
    const [selectedEmpName, setSelectedEmpName] = useState('');
    const [selectedEmpId, setSelectedEmpId] = useState(null);
    const [empDrilldownType, setEmpDrilldownType] = useState('leave'); // 'leave' or 'late'

    const fetchTrendData = async () => {
        try {
            setLoading(true);
            const res = await api.get('/hrms/company-overview-trends', {
                params: { year, user_id: userId }
            });
            if (res.data.status) {
                setTrendData(res.data.data);
            }
        } catch (error) {
            console.error('Error fetching trend data:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchTopLeaveEmployees = async () => {
        try {
            setLeaveTrendsLoading(true);
            const res = await api.get('hrms/top-leave-employees', {
                params: { month: leaveMonth, year: leaveYear, limit: leaveLimit, leave_type: leaveTypeFilter }
            });
            if (res.data.status) {
                setTopLeaveEmployees(res.data.data);
            }
        } catch (error) {
            console.error('Error fetching top leave employees trends:', error);
            toast.error('Failed to load top leave employees trends');
        } finally {
            setLeaveTrendsLoading(false);
        }
    };

    const fetchTopLateComers = async () => {
        try {
            setLateTrendsLoading(true);
            const res = await api.get('hrms/top-late-comers', {
                params: { month: lateMonth, year: lateYear, limit: lateLimit }
            });
            if (res.data.status) {
                setTopLateComers(res.data.data);
            }
        } catch (error) {
            console.error('Error fetching top late comers trends:', error);
            toast.error('Failed to load top late comers trends');
        } finally {
            setLateTrendsLoading(false);
        }
    };

    const openEmpDrilldown = async (empId, empName, type) => {
        try {
            setSelectedEmpId(empId);
            setSelectedEmpName(empName);
            setEmpDrilldownType(type);
            setEmpDrilldownLoading(true);
            setShowEmpDrilldown(true);

            const endpoint = type === 'leave' ? 'hrms/employee-leave-drilldown' : 'hrms/employee-late-drilldown';
            const monthParam = type === 'leave' ? leaveMonth : lateMonth;
            const yearParam = type === 'leave' ? leaveYear : lateYear;

            const res = await api.get(endpoint, {
                params: { user_id: empId, month: monthParam, year: yearParam }
            });

            if (res.data.status) {
                setEmpDrilldownData(res.data.data);
            }
        } catch (error) {
            console.error('Error fetching employee drilldown:', error);
            toast.error('Failed to load employee log details');
        } finally {
            setEmpDrilldownLoading(false);
        }
    };

    const fetchRecordDetails = async (monthIndex, type) => {
        try {
            const month = monthIndex + 1;
            const monthName = trendData.months[monthIndex];
            const typeLabels = {
                'total': 'Total Leaves',
                'unscheduled': 'Unscheduled Leaves',
                'scheduled': 'Scheduled Leaves',
                'permission': 'Permissions'
            };

            setModalInfo({ title: typeLabels[type], month: monthName });
            setActiveTrendType(type);
            setDetailsLoading(true);
            setShowModal(true);

            const res = await api.get('/hrms/trend-record-details', {
                params: { year, month, type, user_id: userId }
            });

            if (res.data.status) {
                setDetails(res.data.data);
            }
        } catch (error) {
            console.error('Error fetching record details:', error);
            toast.error('Failed to load details');
        } finally {
            setDetailsLoading(false);
        }
    };

    useEffect(() => {
        fetchTrendData();
    }, [year, userId]);

    useEffect(() => {
        fetchTopLeaveEmployees();
    }, [leaveMonth, leaveYear, leaveLimit, leaveTypeFilter]);

    useEffect(() => {
        fetchTopLateComers();
    }, [lateMonth, lateYear, lateLimit]);

    const exportToExcel = async () => {
        if (!details.length) return;

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Leave Details');

        const colLetter = activeTrendType === 'permission' ? 'E' : 'F';

        // 1. Company Title
        worksheet.mergeCells(`A1:${colLetter}1`);
        const titleCell = worksheet.getCell('A1');
        titleCell.value = 'eNova Software and Hardware Solutions P Ltd';
        titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
        titleCell.font = { name: 'Arial', size: 16, bold: true, color: { argb: 'FFFF0000' } };

        // 2. Subtitle
        worksheet.mergeCells(`A2:${colLetter}2`);
        const subTitleCell = worksheet.getCell('A2');
        subTitleCell.value = `Organizational Leave Trends - ${modalInfo.title}`;
        subTitleCell.alignment = { vertical: 'middle', horizontal: 'center' };
        subTitleCell.font = { name: 'Arial', size: 12, bold: true };

        // 3. Metadata
        worksheet.mergeCells(`A3:${colLetter}3`);
        const metaCell = worksheet.getCell('A3');
        metaCell.value = `Period: ${modalInfo.month} ${year}`;
        metaCell.alignment = { vertical: 'middle', horizontal: 'center' };
        metaCell.font = { name: 'Arial', size: 10, bold: false };

        // 4. Spacer
        worksheet.addRow([]);

        // 5. Table Headers
        const headers = activeTrendType === 'permission'
            ? ["EMPLOYEE NAME", "LEAVE DATE", "LEAVE TYPE", "HOURS", "REASON"]
            : ["EMPLOYEE NAME", "LEAVE DATE", "LEAVE TYPE", "DURATION", "ALTERNATIVES", "REASON"];
        const headerRow = worksheet.addRow(headers);
        headerRow.height = 25;

        headerRow.eachCell((cell) => {
            cell.font = { bold: true };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFE5E7EB' }
            };
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });

        // 6. Data Rows
        details.forEach(d => {
            const rowData = activeTrendType === 'permission'
                ? [d.employee_name, d.leave_date, d.type, d.duration, d.reason || 'N/A']
                : [d.employee_name, d.leave_date, d.type, d.duration, d.alternate_names || '-', d.reason || 'N/A'];
            const row = worksheet.addRow(rowData);

            const cellLimit = activeTrendType === 'permission' ? 5 : 6;
            for (let i = 1; i <= cellLimit; i++) {
                const cell = row.getCell(i);
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };

                cell.alignment = {
                    vertical: 'middle',
                    horizontal: (i === 2 || i === 3 || i === 4 || i === 5) ? 'center' : 'left',
                    wrapText: i === cellLimit
                };

                cell.font = { size: 10 };
            }
        });

        if (activeTrendType === 'permission') {
            worksheet.columns = [
                { width: 30 }, // Employee
                { width: 20 }, // Date
                { width: 20 }, // Type
                { width: 12 }, // Duration
                { width: 60 }  // Reason
            ];
        } else {
            worksheet.columns = [
                { width: 30 }, // Employee
                { width: 20 }, // Date
                { width: 20 }, // Type
                { width: 12 }, // Duration
                { width: 25 }, // Alternatives
                { width: 60 }  // Reason
            ];
        }

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `Leave_Trends_${modalInfo.month}_${year}.xlsx`);
    };

    const exportLeaveSummaryToExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const leaveSheet = workbook.addWorksheet('Top Leaves Taken');
        
        const typeLabel = leaveTypeFilter === 'scheduled' ? ' (Scheduled)' : (leaveTypeFilter === 'unscheduled' ? ' (Unscheduled)' : '');
        
        // 1. Company Title
        leaveSheet.mergeCells('A1:C1');
        const titleCell = leaveSheet.getCell('A1');
        titleCell.value = 'eNova Software and Hardware Solutions P Ltd';
        titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
        titleCell.font = { name: 'Arial', size: 16, bold: true, color: { argb: 'FFFF0000' } };
        
        // 2. Subtitle
        leaveSheet.mergeCells('A2:C2');
        const subtitleCell = leaveSheet.getCell('A2');
        subtitleCell.value = `Top Leave Taken Employees${typeLabel}`;
        subtitleCell.alignment = { vertical: 'middle', horizontal: 'center' };
        subtitleCell.font = { name: 'Arial', size: 12, bold: true };
        
        // 3. Metadata
        leaveSheet.mergeCells('A3:C3');
        const dateCell = leaveSheet.getCell('A3');
        dateCell.value = `Period: ${getMonthName(leaveMonth)} ${getYearDisplay(leaveYear)}`;
        dateCell.alignment = { vertical: 'middle', horizontal: 'center' };
        dateCell.font = { name: 'Arial', size: 10, bold: false };
        
        // 4. Spacer
        leaveSheet.addRow([]);

        // 5. Table Headers
        const headerRow = leaveSheet.addRow(['EMPLOYEE ID', 'EMPLOYEE NAME', 'TOTAL LEAVES TAKEN (DAYS)']);
        headerRow.height = 25;
        headerRow.eachCell((cell) => {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7EB' } };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });
        
        // 6. Data Rows
        topLeaveEmployees.forEach(emp => {
            const r = leaveSheet.addRow([emp.user_id, emp.name, emp.count]);
            r.height = 20;
            for(let i=1; i<=3; i++) {
                const cell = r.getCell(i);
                cell.font = { size: 10 };
                cell.alignment = {
                    vertical: 'middle',
                    horizontal: (i === 1 || i === 3) ? 'center' : 'left'
                };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            }
        });
        leaveSheet.columns = [{ width: 18 }, { width: 35 }, { width: 28 }];
        
        const buffer = await workbook.xlsx.writeBuffer();
        const cleanMonth = getMonthName(leaveMonth).replace(/ /g, '_');
        const cleanYear = getYearDisplay(leaveYear);
        saveAs(new Blob([buffer]), `Top_Leaves_Summary_${cleanMonth}_${cleanYear}.xlsx`);
        toast.success('Leaves Summary Excel Exported');
    };

    const exportLateSummaryToExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const lateSheet = workbook.addWorksheet('Top Late Comers');
        
        // 1. Company Title
        lateSheet.mergeCells('A1:C1');
        const titleCell = lateSheet.getCell('A1');
        titleCell.value = 'eNova Software and Hardware Solutions P Ltd';
        titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
        titleCell.font = { name: 'Arial', size: 16, bold: true, color: { argb: 'FFFF0000' } };
        
        // 2. Subtitle
        lateSheet.mergeCells('A2:C2');
        const subtitleCell = lateSheet.getCell('A2');
        subtitleCell.value = 'Top Late Comers';
        subtitleCell.alignment = { vertical: 'middle', horizontal: 'center' };
        subtitleCell.font = { name: 'Arial', size: 12, bold: true };
        
        // 3. Metadata
        lateSheet.mergeCells('A3:C3');
        const dateCell = lateSheet.getCell('A3');
        dateCell.value = `Period: ${getMonthName(lateMonth)} ${getYearDisplay(lateYear)}`;
        dateCell.alignment = { vertical: 'middle', horizontal: 'center' };
        dateCell.font = { name: 'Arial', size: 10, bold: false };
        
        // 4. Spacer
        lateSheet.addRow([]);
        
        // 5. Table Headers
        const headerRow = lateSheet.addRow(['EMPLOYEE ID', 'EMPLOYEE NAME', 'LATE COUNT (TIMES)']);
        headerRow.height = 25;
        headerRow.eachCell((cell) => {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7EB' } };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });
        
        // 6. Data Rows
        topLateComers.forEach(emp => {
            const r = lateSheet.addRow([emp.user_id, emp.name, emp.count]);
            r.height = 20;
            for(let i=1; i<=3; i++) {
                const cell = r.getCell(i);
                cell.font = { size: 10 };
                cell.alignment = {
                    vertical: 'middle',
                    horizontal: (i === 1 || i === 3) ? 'center' : 'left'
                };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            }
        });
        lateSheet.columns = [{ width: 18 }, { width: 35 }, { width: 28 }];
        
        const buffer = await workbook.xlsx.writeBuffer();
        const cleanMonth = getMonthName(lateMonth).replace(/ /g, '_');
        const cleanYear = getYearDisplay(lateYear);
        saveAs(new Blob([buffer]), `Top_Late_Comers_Summary_${cleanMonth}_${cleanYear}.xlsx`);
        toast.success('Late Summary Excel Exported');
    };

    const exportEmpDrilldownToExcel = async () => {
        if (!empDrilldownData.length) return;
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Log Details');
        
        const isLeave = empDrilldownType === 'leave';
        const colRange = isLeave ? 'E' : 'D';

        // 1. Company Title
        sheet.mergeCells(`A1:${colRange}1`);
        const titleCell = sheet.getCell('A1');
        titleCell.value = 'eNova Software and Hardware Solutions P Ltd';
        titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
        titleCell.font = { name: 'Arial', size: 16, bold: true, color: { argb: 'FFFF0000' } };
        
        // 2. Subtitle
        sheet.mergeCells(`A2:${colRange}2`);
        const subtitleCell = sheet.getCell('A2');
        subtitleCell.value = isLeave ? `Approved Leaves - ${selectedEmpName}` : `Late Arrivals - ${selectedEmpName}`;
        subtitleCell.alignment = { vertical: 'middle', horizontal: 'center' };
        subtitleCell.font = { name: 'Arial', size: 12, bold: true };
        
        // 3. Metadata
        const mParam = isLeave ? leaveMonth : lateMonth;
        const yParam = isLeave ? leaveYear : lateYear;
        sheet.mergeCells(`A3:${colRange}3`);
        const dateCell = sheet.getCell('A3');
        dateCell.value = `Period: ${getMonthName(mParam)} ${getYearDisplay(yParam)}`;
        dateCell.alignment = { vertical: 'middle', horizontal: 'center' };
        dateCell.font = { name: 'Arial', size: 10, bold: false };
        
        // 4. Spacer
        sheet.addRow([]);
        
        // 5. Table Headers
        const headers = isLeave 
            ? ['DATE', 'LEAVE TYPE', 'DURATION (DAYS)', 'ALTERNATIVES', 'REASON'] 
            : ['DATE', 'STATUS', 'FIRST PUNCH', 'REASON'];
        const headerRow = sheet.addRow(headers);
        headerRow.height = 25;
        headerRow.eachCell((cell) => {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7EB' } };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });
        
        // 6. Data Rows
        empDrilldownData.forEach(row => {
            const dataRow = isLeave
                ? [row.leave_date, row.type, row.duration, row.alternate_names || '-', row.reason || 'N/A']
                : [row.leave_date, row.status, row.first_punch, row.reason || 'Late Arrival'];
            const r = sheet.addRow(dataRow);
            r.getCell(1).alignment = { horizontal: 'center' };
            r.getCell(2).alignment = { horizontal: 'center' };
            r.getCell(3).alignment = { horizontal: 'center' };
            if (isLeave) {
                r.getCell(4).alignment = { horizontal: 'left' };
            }
            
            const cellLimit = isLeave ? 5 : 4;
            for(let i=1; i<=cellLimit; i++) {
                r.getCell(i).border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            }
        });
        
        if (isLeave) {
            sheet.columns = [{ width: 20 }, { width: 20 }, { width: 20 }, { width: 25 }, { width: 50 }];
        } else {
            sheet.columns = [{ width: 20 }, { width: 20 }, { width: 20 }, { width: 50 }];
        }
        
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `${selectedEmpName.replace(/ /g, '_')}_Attendance_Details.xlsx`);
        toast.success('Details Excel Exported');
    };


    const topLeavesChartOptions = {
        chart: {
            type: 'column',
            height: 320,
            spacingTop: 50,
            spacingBottom: 15
        },
        title: { text: null },
        xAxis: {
            categories: topLeaveEmployees.map(d => d.name),
            labels: {
                step: 1,
                style: { fontSize: '11px', fontWeight: 'bold', color: '#64748b', cursor: 'pointer' },
                formatter: function () {
                    return this.value.split(' ')[0]; // Show first name only
                }
            },
            lineWidth: 1,
            lineColor: '#e2e8f0',
            tickWidth: 0
        },
        yAxis: {
            min: 0,
            title: {
                text: 'Leave Days',
                style: { fontSize: '9px', fontWeight: 'bold', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }
            },
            gridLineWidth: 0,
            labels: { enabled: false }
        },
        tooltip: {
            useHTML: true,
            outside: true,
            padding: 0,
            backgroundColor: 'transparent',
            borderWidth: 0,
            shadow: false,
            formatter: function () {
                const emp = topLeaveEmployees[this.point.index];
                return `
                <div style="padding: 12px; background: white; border: 1px solid #e2e8f0; border-radius: 12px; box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1); min-width: 150px;">
                    <b style="font-size: 13px; color: #1e293b; display: block; margin-bottom: 4px;">${emp.name}</b>
                    <div style="color: #ef4444; font-weight: 800; font-size: 11px;">
                        ${this.y} Leaves Taken
                    </div>
                    <span style="font-size: 10px; color: #94a3b8; display: block; margin-top: 4px;">Click to view request logs</span>
                </div>
                `;
            }
        },
        plotOptions: {
            column: {
                borderRadius: 8,
                colorByPoint: true,
                colors: ['#3b82f6', '#10b981', '#f59e0b', '#f43f5e', '#8b5cf6', '#06b6d4', '#ec4899'],
                pointPadding: 0.15,
                groupPadding: 0.1,
                borderWidth: 0,
                cursor: 'pointer',
                point: {
                    events: {
                        click: function () {
                            const emp = topLeaveEmployees[this.index];
                            openEmpDrilldown(emp.user_id, emp.name, 'leave');
                        }
                    }
                },
                dataLabels: {
                    enabled: true,
                    useHTML: true,
                    crop: false,
                    overflow: 'allow',
                    allowOverlap: true,
                    formatter: function () {
                        const emp = topLeaveEmployees[this.point.index];
                        let avatarUrl = null;
                        if (emp?.profile_image) {
                            if (emp.profile_image.startsWith('http')) {
                                avatarUrl = emp.profile_image;
                            } else {
                                const basePath = emp.profile_image.startsWith('uploads/') ? '' : 'uploads/profiles/';
                                avatarUrl = `${process.env.REACT_APP_API_URL || ''}/${basePath}${emp.profile_image}`;
                            }
                        }
                        avatarUrl = avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(emp.name)}&background=random`;
                        return `
                        <div style="text-align: center; display: flex; flex-direction: column; align-items: center; justify-content: center; width: 32px; height: 32px; position: relative; top: -10px;">
                            <div style="width: 28px; height: 28px; border-radius: 50%; border: 2px solid white; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); overflow: hidden; background: #fff; display: flex; align-items: center; justify-content: center;">
                                <img src="${avatarUrl}" style="width: 100%; height: 100%; object-fit: cover;" onerror="this.src='https://ui-avatars.com/api/?name=${encodeURIComponent(emp.name)}&background=random'" />
                            </div>
                            <div style="background: white; color: #475569; font-size: 9px; font-weight: 900; padding: 1px 4px; border-radius: 4px; position: absolute; bottom: -8px; box-shadow: 0 2px 4px rgba(0,0,0,0.05); border: 1px solid #f1f5f9;">${this.y}</div>
                        </div>
                        `;
                    },
                    verticalAlign: 'top',
                    inside: false,
                    padding: 0,
                    y: -45
                }
            }
        },
        series: [{
            name: 'Leaves Taken',
            data: topLeaveEmployees.map(d => d.count),
            showInLegend: false
        }],
        credits: { enabled: false }
    };

    const topLateChartOptions = {
        chart: {
            type: 'column',
            height: 320,
            spacingTop: 50,
            spacingBottom: 15
        },
        title: { text: null },
        xAxis: {
            categories: topLateComers.map(d => d.name),
            labels: {
                step: 1,
                style: { fontSize: '11px', fontWeight: 'bold', color: '#64748b', cursor: 'pointer' },
                formatter: function () {
                    return this.value.split(' ')[0]; // Show first name only
                }
            },
            lineWidth: 1,
            lineColor: '#e2e8f0',
            tickWidth: 0
        },
        yAxis: {
            min: 0,
            title: {
                text: 'Late Count',
                style: { fontSize: '9px', fontWeight: 'bold', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }
            },
            gridLineWidth: 0,
            labels: { enabled: false }
        },
        tooltip: {
            useHTML: true,
            outside: true,
            padding: 0,
            backgroundColor: 'transparent',
            borderWidth: 0,
            shadow: false,
            formatter: function () {
                const emp = topLateComers[this.point.index];
                return `
                <div style="padding: 12px; background: white; border: 1px solid #e2e8f0; border-radius: 12px; box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1); min-width: 150px;">
                    <b style="font-size: 13px; color: #1e293b; display: block; margin-bottom: 4px;">${emp.name}</b>
                    <div style="color: #f59e0b; font-weight: 800; font-size: 11px;">
                        ${this.y} Times Late
                    </div>
                    <span style="font-size: 10px; color: #94a3b8; display: block; margin-top: 4px;">Click to view late logs</span>
                </div>
                `;
            }
        },
        plotOptions: {
            column: {
                borderRadius: 8,
                colorByPoint: true,
                colors: ['#fb7171', '#fcd34d', '#38bdf8', '#818cf8', '#fb923c', '#4ade80'],
                pointPadding: 0.15,
                groupPadding: 0.1,
                borderWidth: 0,
                cursor: 'pointer',
                point: {
                    events: {
                        click: function () {
                            const emp = topLateComers[this.index];
                            openEmpDrilldown(emp.user_id, emp.name, 'late');
                        }
                    }
                },
                dataLabels: {
                    enabled: true,
                    useHTML: true,
                    crop: false,
                    overflow: 'allow',
                    allowOverlap: true,
                    formatter: function () {
                        const emp = topLateComers[this.point.index];
                        let avatarUrl = null;
                        if (emp?.profile_image) {
                            if (emp.profile_image.startsWith('http')) {
                                avatarUrl = emp.profile_image;
                            } else {
                                const basePath = emp.profile_image.startsWith('uploads/') ? '' : 'uploads/profiles/';
                                avatarUrl = `${process.env.REACT_APP_API_URL || ''}/${basePath}${emp.profile_image}`;
                            }
                        }
                        avatarUrl = avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(emp.name)}&background=random`;
                        return `
                        <div style="text-align: center; display: flex; flex-direction: column; align-items: center; justify-content: center; width: 32px; height: 32px; position: relative; top: -10px;">
                            <div style="width: 28px; height: 28px; border-radius: 50%; border: 2px solid white; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); overflow: hidden; background: #fff; display: flex; align-items: center; justify-content: center;">
                                <img src="${avatarUrl}" style="width: 100%; height: 100%; object-fit: cover;" onerror="this.src='https://ui-avatars.com/api/?name=${encodeURIComponent(emp.name)}&background=random'" />
                            </div>
                            <div style="background: white; color: #475569; font-size: 9px; font-weight: 900; padding: 1px 4px; border-radius: 4px; position: absolute; bottom: -8px; box-shadow: 0 2px 4px rgba(0,0,0,0.05); border: 1px solid #f1f5f9;">${this.y}</div>
                        </div>
                        `;
                    },
                    verticalAlign: 'top',
                    inside: false,
                    padding: 0,
                    y: -45
                }
            }
        },
        series: [{
            name: 'Late Count',
            data: topLateComers.map(d => d.count),
            showInLegend: false
        }],
        credits: { enabled: false }
    };

    if (loading && !trendData) {
        return (
            <div className="flex items-center justify-center p-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
            </div>
        );
    }

    const isCurrentYear = year === new Date().getFullYear();
    const displayLimit = isCurrentYear ? trendData.summary.current_month : 12;
    const categories = trendData.months.slice(0, displayLimit);

    const sliceData = (arr) => (arr || []).slice(0, displayLimit);

    const commonOptions = {
        credits: { enabled: false },
        title: { text: null },
        xAxis: {
            categories: categories,
            gridLineWidth: 0,
            lineWidth: 0,
            tickWidth: 0,
            labels: { style: { color: '#94a3b8', fontSize: '10px' } }
        },
        yAxis: {
            title: { text: null },
            gridLineColor: '#f1f5f9',
            labels: { style: { color: '#94a3b8', fontSize: '10px' } }
        },
        legend: {
            align: 'left',
            verticalAlign: 'top',
            itemStyle: { fontSize: '11px', fontWeight: '500', color: '#64748b' },
            symbolRadius: 4
        },
        tooltip: {
            shared: true,
            backgroundColor: '#ffffff',
            borderRadius: 12,
            borderWidth: 1,
            borderColor: '#f1f5f9',
            style: { fontSize: '12px' },
            shadow: { color: 'rgba(0,0,0,0.05)', offsetX: 0, offsetY: 4, opacity: 0.1, width: 12 }
        },
        plotOptions: {
            series: {
                cursor: 'pointer',
                marker: { enabled: true, radius: 4, states: { hover: { enabled: true, radius: 6 } } },
                point: {
                    events: {
                        click: function () {
                            const type = this.series.name === 'Permissions' ? 'permission' :
                                (this.series.name === 'Unscheduled' || this.series.name === 'Unscheduled Leaves' ? 'unscheduled' :
                                    (this.series.name === 'Scheduled' || this.series.name === 'Scheduled Leaves' ? 'scheduled' : 'total'));
                            fetchRecordDetails(this.x, type);
                        }
                    }
                }
            }
        }
    };

    const cardStyle = "bg-white p-6 rounded-[12px] shadow-[0_4px_12px_rgba(0,0,0,0.05)] border border-gray-50 flex flex-col";

    return (
        <div className="w-full py-2">
            <div className="w-full">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 pb-6 border-b border-gray-100">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800">Organizational Leave Trends Overview</h2>
                        <p className="text-sm text-gray-500 mt-1">Cross-organizational attendance patterns</p>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="relative group/select">
                            <select
                                value={year}
                                onChange={(e) => setYear(parseInt(e.target.value))}
                                className="text-xs font-bold text-gray-600 bg-white border border-gray-200 rounded-xl pl-4 pr-10 py-2.5 outline-none focus:ring-2 focus:ring-red-100 transition-all shadow-sm cursor-pointer hover:border-red-200 min-w-[80px]"
                                style={{ WebkitAppearance: 'none', MozAppearance: 'none' }}
                            >
                                {[0, 1, 2].map(i => {
                                    const y = new Date().getFullYear() - i;
                                    return <option key={y} value={y}>{y}</option>
                                })}
                            </select>
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 group-hover/select:text-red-500 transition-colors">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Total Leaves Trend */}
                    <div className={cardStyle}>
                        <h4 className="text-sm font-bold text-gray-700 mb-4">Total Leaves Trend</h4>
                        <div className="flex-1 min-h-[250px]">
                            <HighchartsReact
                                highcharts={Highcharts}
                                options={{
                                    ...commonOptions,
                                    chart: { type: 'line', height: 250 },
                                    series: [
                                        {
                                            name: 'Total Leaves',
                                            data: sliceData(trendData?.total_leaves),
                                            color: '#ef4444',
                                            lineWidth: 3,
                                            marker: { symbol: 'circle' }
                                        },
                                        {
                                            name: 'Unscheduled Leaves',
                                            data: sliceData(trendData?.unscheduled_leaves),
                                            color: '#F97316',
                                            lineWidth: 3,
                                            dashStyle: 'ShortDash',
                                            marker: { symbol: 'diamond' }
                                        }
                                    ]
                                }}
                            />
                        </div>
                        <div className="mt-4 flex flex-col gap-1 border-t border-gray-50 pt-4">
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                                <span className="text-xs font-semibold text-gray-600">Current Month: {trendData?.total_leaves[trendData.summary.current_month - 1] || 0}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                                <span className="text-xs font-semibold text-gray-400">Last Month: {trendData?.total_leaves[trendData.summary.last_month - 1] || 0}</span>
                            </div>
                        </div>
                    </div>

                    {/* Unscheduled Leaves Trend */}
                    <div className={cardStyle}>
                        <h4 className="text-sm font-bold text-gray-700 mb-4">Unscheduled & Scheduled Analysis</h4>
                        <div className="flex-1 min-h-[250px]">
                            <HighchartsReact
                                highcharts={Highcharts}
                                options={{
                                    ...commonOptions,
                                    chart: { type: 'column', height: 250 },
                                    plotOptions: {
                                        ...commonOptions.plotOptions,
                                        column: {
                                            pointPadding: 0.2,
                                            borderWidth: 0,
                                            borderRadius: 4
                                        }
                                    },
                                    series: [
                                        {
                                            name: 'Scheduled',
                                            data: sliceData(trendData?.scheduled_leaves),
                                            color: '#3B82F6',
                                        },
                                        {
                                            name: 'Unscheduled',
                                            data: sliceData(trendData?.unscheduled_leaves),
                                            color: '#F97316',
                                        }
                                    ]
                                }}
                            />
                        </div>
                        <div className="mt-4 flex flex-col gap-1 border-t border-gray-50 pt-4">
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                                <span className="text-xs font-semibold text-gray-600">Scheduled (CM): {trendData?.scheduled_leaves[trendData.summary.current_month - 1] || 0}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                                <span className="text-xs font-semibold text-gray-600">Unscheduled (CM): {trendData?.unscheduled_leaves[trendData.summary.current_month - 1] || 0}</span>
                            </div>
                        </div>
                    </div>

                    {/* Total Permissions Trend */}
                    <div className={cardStyle}>
                        <h4 className="text-sm font-bold text-gray-700 mb-4">Total Permissions Trend</h4>
                        <div className="flex-1 min-h-[250px]">
                            <HighchartsReact
                                highcharts={Highcharts}
                                options={{
                                    ...commonOptions,
                                    chart: { type: 'area', height: 250 },
                                    series: [{
                                        name: 'Permissions',
                                        data: sliceData(trendData?.permissions),
                                        color: '#22C55E',
                                        fillColor: {
                                            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                                            stops: [
                                                [0, 'rgba(34, 197, 94, 0.2)'],
                                                [1, 'rgba(34, 197, 94, 0)']
                                            ]
                                        },
                                        lineWidth: 3,
                                        marker: { symbol: 'circle' }
                                    }]
                                }}
                            />
                        </div>
                        <div className="mt-4 flex flex-col gap-1 border-t border-gray-50 pt-4">
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                <span className="text-xs font-semibold text-gray-600">Current Month: {trendData?.permissions[trendData.summary.current_month - 1] || 0}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                                <span className="text-xs font-semibold text-gray-400">Last Month: {trendData?.permissions[trendData.summary.last_month - 1] || 0}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Newly Added: Leave Trend by Employees Section Header */}
                <div className="mt-10 mb-6">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                        <FiTrendingUp className="text-red-500 animate-pulse" />
                        Leave Trend by Employees
                    </h3>
                    <p className="text-xs text-gray-500 font-semibold mt-1">Ranked employee attendance, leaves, and late punch activities.</p>
                </div>

                {/* Side-by-side independent cards layout (full width) */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">

                    {/* Top Leaves taken Employee Card */}
                    <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.02)] flex flex-col justify-between relative overflow-hidden">
                        {leaveTrendsLoading ? (
                            <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] flex items-center justify-center z-10 rounded-3xl">
                                <div className="animate-spin rounded-full h-10 w-10 border-4 border-red-500/20 border-b-red-500"></div>
                            </div>
                        ) : null}

                        {/* Toolbar header */}
                        <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-3 mb-6 border-b border-gray-50 pb-4">
                            <div>
                                <h4 className="text-sm font-black uppercase tracking-wider text-gray-600">Top Leaves Taken Employees</h4>
                                <span className="text-[10px] font-bold text-red-500 bg-red-50 px-2.5 py-0.5 rounded-full mt-1.5 inline-block">Approved Leaves</span>
                            </div>

                             <div className="flex flex-wrap items-center gap-2 w-full xl:w-auto xl:justify-end">
                                {/* Limit Selector */}
                                <div className="flex items-center gap-1.5 h-9">
                                    <span className="text-[10px] font-black uppercase text-gray-400">Limit:</span>
                                    <select
                                        value={leaveLimit}
                                        onChange={(e) => setLeaveLimit(e.target.value)}
                                        className="text-xs font-bold text-gray-600 bg-white border border-gray-200 rounded-xl px-6 py-1.5 outline-none focus:ring-1 focus:ring-red-100 transition-all shadow-sm cursor-pointer hover:border-red-200 h-9"
                                    >
                                        <option value="5">Top 5</option>
                                        <option value="10">Top 10</option>
                                        <option value="20">Top 20</option>
                                        <option value="all">All</option>
                                    </select>
                                </div>

                                {/* Leave Type Selector */}
                                <select
                                    value={leaveTypeFilter}
                                    onChange={(e) => setLeaveTypeFilter(e.target.value)}
                                    className="text-xs font-bold text-gray-600 bg-white border border-gray-200 rounded-xl px-3 py-1.5 outline-none focus:ring-1 focus:ring-red-100 transition-all shadow-sm cursor-pointer hover:border-red-200 h-9"
                                >
                                    <option value="all">All Leaves</option>
                                    <option value="scheduled">Scheduled</option>
                                    <option value="unscheduled">Unscheduled</option>
                                </select>

                                {/* Month Selector */}
                                <select
                                    value={leaveMonth}
                                    onChange={(e) => setLeaveMonth(e.target.value)}
                                    className="text-xs font-bold text-gray-600 bg-white border border-gray-200 rounded-xl px-3 py-1.5 outline-none focus:ring-1 focus:ring-red-100 transition-all shadow-sm cursor-pointer hover:border-red-200 h-9"
                                >
                                    <option value="all">All Months</option>
                                    <option value="1">January</option>
                                    <option value="2">February</option>
                                    <option value="3">March</option>
                                    <option value="4">April</option>
                                    <option value="5">May</option>
                                    <option value="6">June</option>
                                    <option value="7">July</option>
                                    <option value="8">August</option>
                                    <option value="9">September</option>
                                    <option value="10">October</option>
                                    <option value="11">November</option>
                                    <option value="12">December</option>
                                </select>

                                {/* Year Selector */}
                                <select
                                    value={leaveYear}
                                    onChange={(e) => setLeaveYear(e.target.value)}
                                    className="text-xs font-bold text-gray-600 bg-white border border-gray-200 rounded-xl px-3 py-1.5 outline-none focus:ring-1 focus:ring-red-100 transition-all shadow-sm cursor-pointer hover:border-red-200 h-9"
                                >
                                    <option value="all">All Year</option>
                                    {[0, 1, 2].map(i => {
                                        const y = new Date().getFullYear() - i;
                                        return <option key={y} value={String(y)}>{y}</option>;
                                    })}
                                </select>

                                {/* Export Button */}
                                <button
                                    onClick={exportLeaveSummaryToExcel}
                                    className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-black shadow-sm flex items-center gap-1.5 active:scale-95 transition-all h-9"
                                >
                                    <FiDownload size={13} /> Export
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 min-h-[320px]">
                            {topLeaveEmployees.length > 0 ? (
                                <HighchartsReact highcharts={Highcharts} options={topLeavesChartOptions} />
                            ) : (
                                <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                                    <FiUsers className="text-4xl opacity-20 mb-3" />
                                    <p className="text-[10px] font-black uppercase tracking-wider">No Leaves Logged</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Top Late Comers Card */}
                    <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.02)] flex flex-col justify-between relative overflow-hidden">
                        {lateTrendsLoading ? (
                            <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] flex items-center justify-center z-10 rounded-3xl">
                                <div className="animate-spin rounded-full h-10 w-10 border-4 border-red-500/20 border-b-red-500"></div>
                            </div>
                        ) : null}

                        {/* Toolbar header */}
                        <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-3 mb-6 border-b border-gray-50 pb-4">
                            <div>
                                <h4 className="text-sm font-black uppercase tracking-wider text-gray-600">Top Late Comers</h4>
                                <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-2.5 py-0.5 rounded-full mt-1.5 inline-block">Attendance Late Check-ins</span>
                            </div>

                             <div className="flex flex-wrap items-center gap-2 w-full xl:w-auto xl:justify-end">
                                {/* Limit Selector */}
                                <div className="flex items-center gap-1.5 h-9">
                                    <span className="text-[10px] font-black uppercase text-gray-400">Limit:</span>
                                    <select
                                        value={lateLimit}
                                        onChange={(e) => setLateLimit(e.target.value)}
                                        className="text-xs font-bold text-gray-600 bg-white border border-gray-200 rounded-xl px-6 py-1.5 outline-none focus:ring-1 focus:ring-red-100 transition-all shadow-sm cursor-pointer hover:border-red-200 h-9"
                                    >
                                        <option value="5">Top 5</option>
                                        <option value="10">Top 10</option>
                                        <option value="20">Top 20</option>
                                        <option value="all">All</option>
                                    </select>
                                </div>

                                {/* Month Selector */}
                                <select
                                    value={lateMonth}
                                    onChange={(e) => setLateMonth(e.target.value)}
                                    className="text-xs font-bold text-gray-600 bg-white border border-gray-200 rounded-xl px-3 py-1.5 outline-none focus:ring-1 focus:ring-red-100 transition-all shadow-sm cursor-pointer hover:border-red-200 h-9"
                                >
                                    <option value="all">All Months</option>
                                    <option value="1">January</option>
                                    <option value="2">February</option>
                                    <option value="3">March</option>
                                    <option value="4">April</option>
                                    <option value="5">May</option>
                                    <option value="6">June</option>
                                    <option value="7">July</option>
                                    <option value="8">August</option>
                                    <option value="9">September</option>
                                    <option value="10">October</option>
                                    <option value="11">November</option>
                                    <option value="12">December</option>
                                </select>

                                {/* Year Selector */}
                                <select
                                    value={lateYear}
                                    onChange={(e) => setLateYear(e.target.value)}
                                    className="text-xs font-bold text-gray-600 bg-white border border-gray-200 rounded-xl px-3 py-1.5 outline-none focus:ring-1 focus:ring-red-100 transition-all shadow-sm cursor-pointer hover:border-red-200 h-9"
                                >
                                    <option value="all">All Year</option>
                                    {[0, 1, 2].map(i => {
                                        const y = new Date().getFullYear() - i;
                                        return <option key={y} value={String(y)}>{y}</option>;
                                    })}
                                </select>

                                {/* Export Button */}
                                <button
                                    onClick={exportLateSummaryToExcel}
                                    className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-black shadow-sm flex items-center gap-1.5 active:scale-95 transition-all h-9"
                                >
                                    <FiDownload size={13} /> Export
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 min-h-[320px]">
                            {topLateComers.length > 0 ? (
                                <HighchartsReact highcharts={Highcharts} options={topLateChartOptions} />
                            ) : (
                                <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                                    <FiClock className="text-4xl opacity-20 mb-3" />
                                    <p className="text-[10px] font-black uppercase tracking-wider">No Late Punches Registered</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Drill-down Modal (Original) */}
        {showModal && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[999] p-2 sm:p-4">
                <div className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-300">
                            {/* Modal Header */}
                            <div className="p-4 sm:p-6 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white sticky top-0">
                                <div className="flex items-center gap-3 sm:gap-4">
                                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-red-50 flex items-center justify-center text-red-500 shadow-inner shrink-0">
                                        <FiUsers className="text-lg sm:text-xl" />
                                    </div>
                                    <div className="min-w-0">
                                        <h3 className="text-base sm:text-xl font-black text-gray-800 tracking-tight truncate">{modalInfo.title} Details</h3>
                                        <p className="text-[10px] sm:text-xs text-gray-400 font-medium tracking-wide">Analysis for <span className="text-red-600 font-bold uppercase">{modalInfo.month} {year}</span></p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={exportToExcel}
                                        className="flex items-center justify-center gap-2 px-4 py-2 bg-[#047857] hover:bg-[#064e3b] text-white rounded-lg transition-all text-[11px] font-bold shadow-md active:scale-95"
                                    >
                                        <FiDownload className="text-sm" />
                                        <span>Export</span>
                                    </button>
                                    <button
                                        onClick={() => setShowModal(false)}
                                        className="p-2 hover:bg-gray-100 rounded-xl text-gray-400 hover:text-gray-600 transition-all border border-transparent hover:border-gray-200"
                                    >
                                        <FiX size={18} className="sm:w-5 sm:h-5" />
                                    </button>
                                </div>
                            </div>

                            {/* Modal Content */}
                            <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50/30">
                                {detailsLoading ? (
                                    <div className="flex flex-col items-center justify-center py-12 gap-5">
                                        <div className="animate-spin rounded-full h-10 w-10 border-4 border-red-500/20 border-b-red-500"></div>
                                        <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Loading Records...</p>
                                    </div>
                                ) : details.length > 0 ? (
                                    <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white shadow-xl">
                                        <table className="w-full text-left border-collapse min-w-[500px]">
                                            <thead>
                                                <tr className="bg-gray-50/80">
                                                    <th className="py-4 font-black text-[10px] uppercase tracking-[0.2em] text-gray-500 px-6 border-b border-gray-100">Employee</th>
                                                    <th className="py-4 font-black text-[10px] uppercase tracking-[0.2em] text-gray-500 px-6 border-b border-gray-100">Date Range</th>
                                                    <th className="py-4 font-black text-[10px] uppercase tracking-[0.2em] text-gray-500 px-6 border-b border-gray-100">Leave Type</th>
                                                    {activeTrendType !== 'permission' && (
                                                        <th className="py-4 font-black text-[10px] uppercase tracking-[0.2em] text-gray-500 px-6 border-b border-gray-100">Alternatives</th>
                                                    )}
                                                    <th className="py-4 font-black text-[10px] uppercase tracking-[0.2em] text-gray-500 px-6 border-b border-gray-100 text-center">{activeTrendType === 'permission' ? 'Hours' : 'Days'}</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {details.map((row, idx) => (
                                                    <tr key={idx} className="hover:bg-red-50/40 transition-all duration-300 group">
                                                        <td className="py-3 px-6">
                                                            <span className="font-bold text-gray-800 group-hover:text-red-700 transition-colors text-xs sm:text-[14px]">{row.employee_name}</span>
                                                        </td>
                                                        <td className="py-3 px-6 text-[10px] sm:text-[12px] text-gray-600 font-semibold whitespace-nowrap">
                                                            {row.leave_date}
                                                        </td>
                                                        <td className="py-3 px-6">
                                                            <span className="px-3 py-1 bg-gray-100/80 text-[9px] font-black uppercase rounded text-gray-500 whitespace-nowrap border border-gray-200/50">
                                                                {row.type}
                                                            </span>
                                                        </td>
                                                        {activeTrendType !== 'permission' && (
                                                            <td className="py-3 px-6 text-[11px] sm:text-xs text-gray-600 font-medium">
                                                                {row.alternate_names || '-'}
                                                            </td>
                                                        )}
                                                        <td className="py-3 px-6 text-center">
                                                            <span className="inline-block px-3 py-1 bg-red-50 text-red-600 rounded-lg font-black text-[10px] shadow-sm shadow-red-100/50 border border-red-100/50">
                                                                {activeTrendType === 'permission' ? row.duration : parseFloat(row.duration).toFixed(1)}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-12 sm:py-24 text-gray-300 bg-white rounded-2xl sm:rounded-3xl border border-dashed border-gray-200">
                                        <FiUsers className="mb-4 sm:mb-6 opacity-20 text-4xl sm:text-6xl" />
                                        <p className="font-black text-[10px] sm:text-xs uppercase tracking-widest text-gray-400 text-center">No Records Found</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Drill-down Modal (New: Employee Leave/Late details) */}
        {showEmpDrilldown && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[999] p-2 sm:p-4">
                <div className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-300">
                            {/* Modal Header */}
                            <div className="p-4 sm:p-6 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white sticky top-0">
                                <div className="flex items-center gap-3 sm:gap-4">
                                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-red-50 flex items-center justify-center text-red-500 shadow-inner shrink-0">
                                        {empDrilldownType === 'leave' ? <FiUsers className="text-lg sm:text-xl" /> : <FiClock className="text-lg sm:text-xl" />}
                                    </div>
                                    <div className="min-w-0">
                                        <h3 className="text-base sm:text-xl font-black text-gray-800 tracking-tight truncate">
                                            {empDrilldownType === 'leave' ? 'Approved Leaves' : 'Late Check-ins'} - {selectedEmpName}
                                        </h3>
                                        <p className="text-[10px] sm:text-xs text-gray-400 font-medium tracking-wide">
                                            Analysis for <span className="text-red-600 font-bold uppercase">
                                                {empDrilldownType === 'leave' 
                                                    ? `${getMonthName(leaveMonth)} ${getYearDisplay(leaveYear)}` 
                                                    : `${getMonthName(lateMonth)} ${getYearDisplay(lateYear)}`}
                                            </span>
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={exportEmpDrilldownToExcel}
                                        className="flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-all text-[11px] font-bold shadow-md active:scale-95"
                                    >
                                        <FiDownload className="text-sm" />
                                        <span>Export</span>
                                    </button>
                                    <button
                                        onClick={() => setShowEmpDrilldown(false)}
                                        className="p-2 hover:bg-gray-100 rounded-xl text-gray-400 hover:text-gray-600 transition-all border border-transparent hover:border-gray-200"
                                    >
                                        <FiX size={18} className="sm:w-5 sm:h-5" />
                                    </button>
                                </div>
                            </div>

                            {/* Modal Content */}
                            <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50/30">
                                {empDrilldownLoading ? (
                                    <div className="flex flex-col items-center justify-center py-12 gap-5">
                                        <div className="animate-spin rounded-full h-10 w-10 border-4 border-red-500/20 border-b-red-500"></div>
                                        <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Loading Records...</p>
                                    </div>
                                ) : empDrilldownData.length > 0 ? (
                                    <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white shadow-xl">
                                        <table className="w-full text-left border-collapse min-w-[500px]">
                                            <thead>
                                                <tr className="bg-gray-50/80">
                                                    <th className="py-4 font-black text-[10px] uppercase tracking-[0.2em] text-gray-500 px-6 border-b border-gray-100">Date</th>
                                                    <th className="py-4 font-black text-[10px] uppercase tracking-[0.2em] text-gray-500 px-6 border-b border-gray-100 text-center">
                                                        {empDrilldownType === 'leave' ? 'Leave Type' : 'Status'}
                                                    </th>
                                                    <th className="py-4 font-black text-[10px] uppercase tracking-[0.2em] text-gray-500 px-6 border-b border-gray-100 text-center">
                                                        {empDrilldownType === 'leave' ? 'Duration (Days)' : 'Punch-In'}
                                                    </th>
                                                    {empDrilldownType === 'leave' && (
                                                        <th className="py-4 font-black text-[10px] uppercase tracking-[0.2em] text-gray-500 px-6 border-b border-gray-100">Alternatives</th>
                                                    )}
                                                    <th className="py-4 font-black text-[10px] uppercase tracking-[0.2em] text-gray-500 px-6 border-b border-gray-100">Reason / Description</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {empDrilldownData.map((row, idx) => (
                                                    <tr key={idx} className="hover:bg-red-50/40 transition-all duration-300 group">
                                                        <td className="py-3 px-6 text-[11px] sm:text-xs text-gray-600 font-bold whitespace-nowrap">
                                                            {row.leave_date}
                                                        </td>
                                                        <td className="py-3 px-6 text-center">
                                                            <span className={`px-3 py-1 text-[9px] font-black uppercase rounded whitespace-nowrap border ${
                                                                empDrilldownType === 'leave' 
                                                                    ? 'bg-gray-100 border-gray-200 text-gray-500' 
                                                                    : 'bg-amber-50 border-amber-200 text-amber-600 shadow-sm'
                                                            }`}>
                                                                {empDrilldownType === 'leave' ? row.type : row.status}
                                                            </span>
                                                        </td>
                                                        <td className="py-3 px-6 text-center">
                                                            <span className={`inline-block px-3 py-1 rounded-lg font-black text-[10px] shadow-sm border ${
                                                                empDrilldownType === 'leave'
                                                                    ? 'bg-red-50 border-red-100 text-red-600 shadow-red-100/50'
                                                                    : 'bg-blue-50 border-blue-100 text-blue-600 shadow-blue-100/50'
                                                            }`}>
                                                                {empDrilldownType === 'leave' ? parseFloat(row.duration).toFixed(1) : row.first_punch}
                                                            </span>
                                                        </td>
                                                        {empDrilldownType === 'leave' && (
                                                            <td className="py-3 px-6 text-[11px] sm:text-xs text-gray-600 font-medium whitespace-nowrap">
                                                                {row.alternate_names || '-'}
                                                            </td>
                                                        )}
                                                        <td className="py-3 px-6">
                                                            <span className="text-[11px] sm:text-xs text-gray-600 font-medium leading-relaxed">
                                                                {row.reason || (empDrilldownType === 'leave' ? 'N/A' : 'Late Arrival')}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-12 sm:py-24 text-gray-300 bg-white rounded-2xl sm:rounded-3xl border border-dashed border-gray-200">
                                        {empDrilldownType === 'leave' ? <FiUsers className="mb-4 sm:mb-6 opacity-20 text-4xl sm:text-6xl" /> : <FiClock className="mb-4 sm:mb-6 opacity-20 text-4xl sm:text-6xl" />}
                                        <p className="font-black text-[10px] sm:text-xs uppercase tracking-widest text-gray-400 text-center">No Records Found</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CompanyOverviewCharts;
